import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import attendanceService from '../services/attendanceService';

const HRAttendanceDashboard = () => {
  const { user } = useAuth();
  const canAccessHroAttendance = ['HRO', 'CHAIRMAN'].includes(user?.role);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [hrEmployees, setHrEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    date: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    hrId: '',
    page: 1,
    limit: 20
  });
  const [pagination, setPagination] = useState({});
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  const normalizeValue = (value) => (value ? String(value).trim() : '');

  const getEmployeeKey = (employee) => normalizeValue(employee?._id || employee?.id || employee?.userId);

  const getEmployeeName = (employee) => {
    const fullName = normalizeValue(employee?.fullName);
    const name = normalizeValue(employee?.name);
    const firstName = normalizeValue(employee?.firstName);
    const lastName = normalizeValue(employee?.lastName);

    if (fullName) return fullName;
    if (name) return name;
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();

    return '';
  };

  const getEmployeeCode = (employee) => {
    const employeeId = normalizeValue(employee?.employeeId);
    const jobId = normalizeValue(employee?.jobId);
    const username = normalizeValue(employee?.username);

    return employeeId || jobId || username || '';
  };

  const employeeDirectory = useMemo(() => {
    const directory = new Map();

    hrEmployees.forEach((employee) => {
      const key = getEmployeeKey(employee);
      if (key) {
        directory.set(key, employee);
      }
    });

    return directory;
  }, [hrEmployees]);

  const anonymousEmployeeLabels = useMemo(() => {
    const labels = new Map();
    let counter = 1;

    const assignIfAnonymous = (employee) => {
      if (!employee) return;

      const key = getEmployeeKey(employee);
      if (!key) return;

      const mappedEmployee = employeeDirectory.get(key) || employee;
      const hasName = Boolean(getEmployeeName(mappedEmployee));
      const hasCode = Boolean(getEmployeeCode(mappedEmployee));

      if (hasName || hasCode) return;

      if (!labels.has(key)) {
        labels.set(key, `employee${counter}`);
        counter += 1;
      }
    };

    hrEmployees.forEach((employee) => assignIfAnonymous(employee));
    attendanceRecords.forEach((record) => assignIfAnonymous(record?.hrId));

    return labels;
  }, [hrEmployees, attendanceRecords, employeeDirectory]);

  const getEmployeeIdentity = (employee) => {
    const key = getEmployeeKey(employee);
    const mappedEmployee = (key && employeeDirectory.get(key)) || employee || {};

    const name = getEmployeeName(mappedEmployee);
    const employeeCode = getEmployeeCode(mappedEmployee);

    if (name || employeeCode) {
      return {
        primary: name || `Employee ${employeeCode}`,
        secondary: employeeCode || 'N/A'
      };
    }

    return {
      primary: (key && anonymousEmployeeLabels.get(key)) || 'employee',
      secondary: 'N/A'
    };
  };

  useEffect(() => {
    if (canAccessHroAttendance) {
      fetchHREmployees();
      fetchAttendanceData();
    }
  }, [canAccessHroAttendance, filters]);

  const fetchHREmployees = async () => {
    try {
      const response = await attendanceService.getHREmployeesList();
      if (response.success) {
        setHrEmployees(response.data);
      }
    } catch (err) {
      console.error('Failed to load HR employees:', err);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching attendance with filters:', filters);
      const response = await attendanceService.getAllHRAttendanceWithLeaves(filters);
      console.log('📊 Attendance response:', response);

      if (response.success) {
        setAttendanceRecords(response.data.records);
        setPagination(response.data.pagination);
        setMonthlyStats({ statusSummary: response.data.statusSummary || [] });
        console.log('✅ Loaded records:', response.data.records.length);
      }
    } catch (err) {
      console.error('❌ Fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filters change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const clearFilters = () => {
    setFilters({
      date: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      hrId: '',
      page: 1,
      limit: 20
    });
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');
      await attendanceService.exportAttendanceToExcel({
        date: filters.date,
        month: filters.month,
        year: filters.year,
        hrId: filters.hrId
      });
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || 'Failed to export attendance data');
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Present': 'bg-green-100 text-green-800',
      'Late': 'bg-orange-100 text-orange-800',
      'Absent': 'bg-red-100 text-red-800',
      'HalfDay': 'bg-blue-100 text-blue-800',
      'OnLeave': 'bg-purple-100 text-purple-800',
      'HalfDayLeave': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (!canAccessHroAttendance) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HRO and Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">HR Attendance Dashboard</h1>
          <p className="text-gray-600">Monitor and manage HR employee attendance</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Monthly Statistics */}
        {monthlyStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {monthlyStats.statusSummary.map(stat => (
              <div key={stat._id} className="bg-white rounded-lg shadow-md p-6">
                <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-2 ${getStatusColor(stat._id)}`}>
                  {stat._id}
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.count}</p>
                <p className="text-gray-600 text-sm">Total Records</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select
                name="month"
                value={filters.month}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <select
                name="year"
                value={filters.year}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HR Employee</label>
              <select
                name="hrId"
                value={filters.hrId}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Employees</option>
                {hrEmployees.map((employee) => {
                  const identity = getEmployeeIdentity(employee);
                  return (
                    <option key={employee._id} value={employee._id}>
                      {identity.primary} ({identity.secondary})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="bg-green-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Exporting...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Attendance Records</h2>
            <p className="text-sm text-gray-600 mt-1">
              Showing {attendanceRecords.length} of {pagination.total || 0} records
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading attendance data...</p>
            </div>
          ) : attendanceRecords.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600">No attendance records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HR Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punch In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Punch Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceRecords.map((record) => {
                    const identity = getEmployeeIdentity(record.hrId);
                    return (
                      <tr key={record._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {identity.primary}
                            </div>
                            <div className="text-sm text-gray-500">
                              {identity.secondary}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(record.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.isLeave ? '🏖️ Leave' : formatTime(record.punchIn)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.isLeave ? '🏖️ Leave' : formatTime(record.punchOut)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {record.totalHours ? `${record.totalHours}h` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(record.status)}`}
                            title={record.isLeave ? `Leave Reason: ${record.leaveReason}` : ''}
                          >
                            {record.status === 'OnLeave' ? 'Full Day Leave' :
                              record.status === 'HalfDayLeave' ? 'Half Day Leave' :
                                record.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.pages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default HRAttendanceDashboard;